import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '../../components';
import { usePageContext } from '../../contexts';
import { useReportContext } from '../../contexts';
import { useIntelligence } from '../../hooks';
import { HandDrawnCircle } from '../../components/HandDrawnCircle';

export const ReportsSection = () => {
  const { navigateTo } = usePageContext();
  const { viewReport, clearReport } = useReportContext();
  const { reports, isLoading } = useIntelligence();
  const latestReports = reports.slice(0, 4);

  return (
    <section id="reports" className="pt-32 pb-40 sm:pt-48 sm:pb-40 bg-[#fcfaf7] text-zinc-950 border-t border-zinc-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-24 sm:mb-40 text-center">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl md:text-5xl  leading-tight tracking-tight text-zinc-900 max-w-3xl mx-auto">
           Capital and <HandDrawnCircle className="text-purple-600">Intelligence</HandDrawnCircle> for what comes next.
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-zinc-500 max-w-2xl mx-auto">
            The future is not about escaping reality but restoring it.
          </p>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {isLoading && (
          <p className="mb-8 text-center text-zinc-400 font-mono text-xs tracking-[0.2em] uppercase">
            Loading latest intelligence...
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {latestReports.map((report, index) => (
            <Reveal key={report.id} delay={index * 100}>
              <div
                onClick={() => {
                  viewReport(report);
                  navigateTo('intelligence');
                }}
                className="group relative aspect-[4/5] sm:aspect-square overflow-hidden cursor-pointer"
              >
                {/* Background Image */}
                <img
                  src={report.image}
                  alt={report.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlays */}
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>

                {/* Category Label */}
                <div className="absolute top-6 left-6 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                  <span className="text-[10px] font-bold tracking-[0.2em] text-white uppercase opacity-80">
                    {report.category}
                  </span>
                </div>

                {/* Content Overlay */}
                <div className="absolute inset-x-8 bottom-8 text-white">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2 leading-tight group-hover:translate-y-[-4px] transition-transform duration-500">
                    {report.title}
                  </h3>
                  <div className="flex flex-col gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    <p className="text-sm font-light italic">
                      {report.subtitle}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest font-bold">
                      by {report.author}
                    </p>
                  </div>
                </div>

                {/* Corner Arrow (Optional but nice) */}
                <div className="absolute bottom-8 right-8 w-10 h-10 border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500 scale-75 group-hover:scale-100 bg-white/10 backdrop-blur-sm">
                  <ArrowRight size={18} className="text-white" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 text-center">
          <button
            onClick={() => {
              clearReport();
              navigateTo('intelligence');
            }}
            className="inline-flex items-center gap-3 text-zinc-500 hover:text-zinc-900 transition-all uppercase text-xs font-bold tracking-[0.3em] group"
          >
            <span>See more Intelligence</span>
            <div className="w-8 h-[1px] bg-zinc-300 group-hover:w-12 group-hover:bg-zinc-900 transition-all duration-500"></div>
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};
